import React, { useState, useCallback } from 'react';
import {
  Globe, Search, CheckCircle, XCircle, AlertTriangle, User, Store,
  Shield, Mail, CreditCard, Calendar, ArrowRight, Loader, RefreshCw,
  Building, Phone, MapPin, DollarSign, FileText, Check
} from 'lucide-react';
import './MerchantCurrencyManagement.css';

const CURRENCY_INFO = {
  NGN: { name: 'Nigerian Naira', symbol: '₦', country: 'Nigeria', flag: '🇳🇬' },
  GHS: { name: 'Ghanaian Cedi', symbol: 'GH₵', country: 'Ghana', flag: '🇬🇭' },
  KES: { name: 'Kenyan Shilling', symbol: 'KSh', country: 'Kenya', flag: '🇰🇪' },
  TZS: { name: 'Tanzanian Shilling', symbol: 'TSh', country: 'Tanzania', flag: '🇹🇿' },
  UGX: { name: 'Ugandan Shilling', symbol: 'USh', country: 'Uganda', flag: '🇺🇬' }
};

const MerchantCurrencyManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [newCurrency, setNewCurrency] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [reason, setReason] = useState('');
  const [changing, setChanging] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Search for merchants
  const searchMerchants = async () => {
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setSelectedMerchant(null);
      setCompliance(null);
      setSuccessMessage('');

      const res = await fetch(`/api/admin/merchants?search=${encodeURIComponent(searchQuery)}&limit=10`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.merchants || []);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  // Select a merchant and load their compliance status
  const selectMerchant = async (merchant) => {
    setSelectedMerchant(merchant);
    setNewCurrency('');
    setNewCountry('');
    setReason('');
    setSuccessMessage('');
    await loadComplianceStatus(merchant.id);
  };

  // Load compliance status for a merchant
  const loadComplianceStatus = async (merchantId) => {
    try {
      setLoadingCompliance(true);
      const res = await fetch(`/api/admin/currency-management/${merchantId}/compliance`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setCompliance(data);
      }
    } catch (err) {
      console.error('Compliance check error:', err);
    } finally {
      setLoadingCompliance(false);
    }
  };

  // Change merchant's currency
  const changeCurrency = async () => {
    if (!selectedMerchant || !newCurrency || !reason.trim()) {
      alert('Please select a new currency and provide a reason');
      return;
    }

    if (!window.confirm(`Are you sure you want to change ${selectedMerchant.email}'s currency from ${selectedMerchant.currency} to ${newCurrency}?`)) {
      return;
    }

    try {
      setChanging(true);
      const res = await fetch(`/api/admin/currency-management/${selectedMerchant.id}/change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          newCurrency,
          newCountry: CURRENCY_INFO[newCurrency]?.country || newCountry,
          reason: reason.trim()
        })
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccessMessage(`Currency successfully changed from ${selectedMerchant.currency} to ${newCurrency}`);
        // Update local state
        setSelectedMerchant(prev => ({ ...prev, currency: newCurrency, country: CURRENCY_INFO[newCurrency]?.country }));
        setNewCurrency('');
        setReason('');
        // Refresh compliance
        await loadComplianceStatus(selectedMerchant.id);
      } else {
        alert(data.error || 'Failed to change currency');
      }
    } catch (err) {
      console.error('Change currency error:', err);
      alert('Failed to change currency');
    } finally {
      setChanging(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAvailableCurrencies = () => {
    if (!selectedMerchant) return [];
    return Object.keys(CURRENCY_INFO).filter(c => c !== selectedMerchant.currency);
  };

  return (
    <div className="merchant-currency-management">
      {/* Header */}
      <div className="mcm-header">
        <div className="mcm-title">
          <Globe size={28} />
          <div>
            <h1>Merchant Currency Management</h1>
            <p>Change merchant's local currency after verifying compliance</p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="mcm-search-section">
        <h2><Search size={20} /> Find Merchant</h2>
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by email, username, or store name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchMerchants()}
          />
          <button onClick={searchMerchants} disabled={searching}>
            {searching ? <Loader className="spin" size={18} /> : <Search size={18} />}
            Search
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map(merchant => (
              <div
                key={merchant.id}
                className={`merchant-result ${selectedMerchant?.id === merchant.id ? 'selected' : ''}`}
                onClick={() => selectMerchant(merchant)}
              >
                <div className="merchant-avatar">
                  {merchant.store?.logo ? (
                    <img src={merchant.store.logo} alt="" />
                  ) : (
                    <Store size={20} />
                  )}
                </div>
                <div className="merchant-info">
                  <span className="merchant-name">{merchant.store?.name || merchant.username}</span>
                  <span className="merchant-email">{merchant.email}</span>
                </div>
                <div className="merchant-currency">
                  <span className="flag">{CURRENCY_INFO[merchant.currency]?.flag}</span>
                  <span className="code">{merchant.currency}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Merchant Details */}
      {selectedMerchant && (
        <div className="mcm-details">
          {/* Success Message */}
          {successMessage && (
            <div className="success-banner">
              <CheckCircle size={20} />
              {successMessage}
            </div>
          )}

          {/* Merchant Info */}
          <div className="detail-card">
            <h3><User size={18} /> Merchant Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <Mail size={16} />
                <span className="label">Email</span>
                <span className="value">{selectedMerchant.email}</span>
              </div>
              <div className="info-item">
                <User size={16} />
                <span className="label">Username</span>
                <span className="value">{selectedMerchant.username}</span>
              </div>
              <div className="info-item">
                <Store size={16} />
                <span className="label">Store</span>
                <span className="value">{selectedMerchant.store?.name || 'No store'}</span>
              </div>
              <div className="info-item">
                <CreditCard size={16} />
                <span className="label">Tier</span>
                <span className="value">{selectedMerchant.merchantTier}</span>
              </div>
              <div className="info-item">
                <MapPin size={16} />
                <span className="label">Country</span>
                <span className="value">{selectedMerchant.country}</span>
              </div>
              <div className="info-item">
                <Calendar size={16} />
                <span className="label">Joined</span>
                <span className="value">{formatDate(selectedMerchant.createdAt)}</span>
              </div>
            </div>

            {/* Current Currency */}
            <div className="current-currency">
              <span className="label">Current Currency</span>
              <div className="currency-display">
                <span className="flag">{CURRENCY_INFO[selectedMerchant.currency]?.flag}</span>
                <span className="code">{selectedMerchant.currency}</span>
                <span className="name">{CURRENCY_INFO[selectedMerchant.currency]?.name}</span>
              </div>
            </div>
          </div>

          {/* Compliance Status */}
          <div className="detail-card">
            <h3>
              <Shield size={18} /> Compliance Status
              <button className="refresh-btn" onClick={() => loadComplianceStatus(selectedMerchant.id)} disabled={loadingCompliance}>
                <RefreshCw size={14} className={loadingCompliance ? 'spin' : ''} />
              </button>
            </h3>

            {loadingCompliance ? (
              <div className="loading-compliance">
                <Loader className="spin" size={24} />
                <span>Checking compliance...</span>
              </div>
            ) : compliance ? (
              <>
                <div className="compliance-score">
                  <div className={`score-circle ${compliance.score >= 100 ? 'passed' : compliance.score >= 75 ? 'warning' : 'failed'}`}>
                    {compliance.score}%
                  </div>
                  <div className="score-text">
                    {compliance.allPassed ? (
                      <span className="passed"><CheckCircle size={16} /> All checks passed - Ready for currency change</span>
                    ) : (
                      <span className="warning"><AlertTriangle size={16} /> Some checks failed - Review before proceeding</span>
                    )}
                  </div>
                </div>

                <div className="compliance-checks">
                  {compliance.checks && Object.entries(compliance.checks).map(([key, check]) => (
                    <div key={key} className={`check-item ${check.passed ? 'passed' : 'failed'}`}>
                      {check.passed ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      <span className="check-name">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </span>
                      {check.count !== undefined && <span className="check-count">({check.count})</span>}
                    </div>
                  ))}
                </div>

                {/* Compliance Requirements */}
                <div className="compliance-requirements">
                  <h4>Requirements for Currency Change:</h4>
                  <ul>
                    <li><strong>KYC Level 3:</strong> Full identity verification completed</li>
                    <li><strong>BVN Verified:</strong> Bank Verification Number confirmed</li>
                    <li><strong>Bank Account Verified:</strong> Account details validated</li>
                    <li><strong>No Pending Settlements:</strong> All payouts must be cleared</li>
                    <li><strong>No Active Orders:</strong> All orders completed or cancelled</li>
                    <li><strong>Email Verified:</strong> Email address confirmed</li>
                    <li><strong>Account Standing:</strong> Not banned or suspended</li>
                  </ul>
                </div>
              </>
            ) : (
              <p className="no-compliance">Unable to load compliance status</p>
            )}
          </div>

          {/* Change Currency Form */}
          <div className="detail-card change-form">
            <h3><DollarSign size={18} /> Change Currency</h3>

            {!compliance?.allPassed && (
              <div className="warning-banner">
                <AlertTriangle size={18} />
                <span>Warning: Not all compliance checks have passed. Proceed with caution.</span>
              </div>
            )}

            <div className="form-group">
              <label>New Currency</label>
              <select
                value={newCurrency}
                onChange={(e) => {
                  setNewCurrency(e.target.value);
                  setNewCountry(CURRENCY_INFO[e.target.value]?.country || '');
                }}
              >
                <option value="">Select new currency...</option>
                {getAvailableCurrencies().map(code => (
                  <option key={code} value={code}>
                    {CURRENCY_INFO[code].flag} {code} - {CURRENCY_INFO[code].name} ({CURRENCY_INFO[code].country})
                  </option>
                ))}
              </select>
            </div>

            {newCurrency && (
              <div className="currency-preview">
                <div className="from">
                  <span className="label">From</span>
                  <span className="flag">{CURRENCY_INFO[selectedMerchant.currency]?.flag}</span>
                  <span className="code">{selectedMerchant.currency}</span>
                </div>
                <ArrowRight size={24} />
                <div className="to">
                  <span className="label">To</span>
                  <span className="flag">{CURRENCY_INFO[newCurrency]?.flag}</span>
                  <span className="code">{newCurrency}</span>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Reason for Change <span className="required">*</span></label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Merchant relocated business to Ghana, requested via support ticket #12345"
                rows={3}
              />
              <span className="hint">Document why this change is being made (for audit trail)</span>
            </div>

            <button
              className="change-btn"
              onClick={changeCurrency}
              disabled={!newCurrency || !reason.trim() || changing}
            >
              {changing ? (
                <>
                  <Loader className="spin" size={18} />
                  Processing...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Change Currency
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantCurrencyManagement;
